import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ApiService } from '@quorum/api';
import {
  AllExtendedWarranty,
  AppointmentCase,
  CampaignHeader,
  Employee,
  ServiceAppointment,
  ServiceGroup,
  TechnicianGroup,
  Vehicle,
  WarrantySubmissionType,
} from '@quorum/models/xs-resource';
import { CustomValidators } from '@quorum/sha-validators';
import { createFormGroupFromObject } from 'libs/sha-services/src/lib/createFormGroupFromObject';
import moment from 'moment';
import { BehaviorSubject, combineLatest, Subject, Subscription } from 'rxjs';
import { debounceTime, filter, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import createNumberMask from 'text-mask-addons/dist/createNumberMask';

@Component({
  selector: 'quorum-appointment-case-create-edit-dialog',
  templateUrl: './appointment-case-create-edit-dialog.component.html',
  styleUrls: ['./appointment-case-create-edit-dialog.component.scss'],
})
export class AppointmentCaseCreateEditDialogComponent implements OnInit, OnDestroy {
  appointmentCaseForm: FormGroup;
  formSubmitted = false;

  filteredServiceGroups: ServiceGroup[];
  tenthsMask = createNumberMask({
    prefix: '',
    allowDecimal: true,
    requireDecimal: true,
    decimalLimit: 2,
    allowNegative: false,
  });
  caseFormOpCodeSub$: Subscription;
  warrantySubmissionTypeSub$: Subscription;
  basicWarrantyExpired$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  caseFormCampaignNumberSub$: Subscription;
  destroy$ = new Subject();

  constructor(
    public dialogRef: MatDialogRef<AppointmentCaseCreateEditDialogComponent>,
    @Inject(MAT_DIALOG_DATA)
    public dialogData: {
      serviceGroups: ServiceGroup[];
      authenticatedEmployee: Employee;
      vehicle: Vehicle;
      appointment: ServiceAppointment;
      dropdowns: any;
      gmServiceFromTree?: any;
      caseToEdit?: AppointmentCase;
    },
    private apiService: ApiService
  ) {
    if (this.dialogData.caseToEdit) {
      this.appointmentCaseForm = createFormGroupFromObject({
        ...this.dialogData.caseToEdit,
      });
      this.checkTenths(['estimatedTime', 'recommendedHours']);
    } else {
      this.appointmentCaseForm = createFormGroupFromObject({
        ...new AppointmentCase(),
      });
    }

    this.appointmentCaseForm.addControl('techOrTechGroup', new FormControl(null));

    if (this.dialogData.caseToEdit) {
      if (this.dialogData.caseToEdit.technicianGroupId) {
        this.appointmentCaseForm.get('techOrTechGroup')?.setValue(this.dialogData.caseToEdit.technicianGroupId);
      }
      if (this.dialogData.caseToEdit.technicianId) {
        this.appointmentCaseForm.get('techOrTechGroup')?.setValue(this.dialogData.caseToEdit.technicianId);
      }
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnInit() {
    this.setFormValidation();

    /**
     * Filter service groups for the current rooftop and shared rooftop (id -1) only
     */
    this.filteredServiceGroups = this.dialogData.serviceGroups.filter(
      (sg) => sg.rooftopId == this.dialogData.authenticatedEmployee.rooftopId || sg.rooftopId == -1
    );

    /**
     * Pre-populate case form with any of the details we have from the appointment
     */
    if (this.dialogData.appointment !== null) {
      this.appointmentCaseForm.get('appointmentId')?.setValue(this.dialogData.appointment.id);

      // Only default tech/tech group from the appointment when creating a new case.
      // When editing, the line item already has its own tech/tech group set in the constructor.
      if (!this.dialogData.caseToEdit) {
        if (this.dialogData.appointment.technicianGroupId) {
          this.appointmentCaseForm.get('technicianGroupId')?.setValue(this.dialogData.appointment.technicianGroupId);
          this.appointmentCaseForm.get('techOrTechGroup')?.setValue(this.dialogData.appointment.technicianGroupId);
        }
        if (this.dialogData.appointment.technicianId) {
          this.appointmentCaseForm.get('technicianId')?.setValue(this.dialogData.appointment.technicianId);
          this.appointmentCaseForm.get('techOrTechGroup')?.setValue(this.dialogData.appointment.technicianId);
        }
      }
    }

    /**
     * When user enters an opCode, look for a Service Group for that
     * opCode and if it exists populate form with the values
     */
    this.caseFormOpCodeSub$ = this.appointmentCaseForm
      .get('opCode')
      ?.valueChanges.pipe(filter((opCode) => opCode && opCode.length > 0))
      .pipe(
        takeUntil(this.destroy$),
        map((opCode) => {
          return this.filteredServiceGroups.find(
            (serviceGroup) =>
              serviceGroup.workOrderLocationOpCode === opCode &&
              (serviceGroup.rooftopId === this.dialogData.authenticatedEmployee.rooftopId ||
                serviceGroup.rooftopId == -1)
          );
        })
      )
      .subscribe((serviceGroup: ServiceGroup) => {
        if (serviceGroup) {
          if (serviceGroup.technicianId) {
            this.appointmentCaseForm.get('techOrTechGroup')?.setValue(serviceGroup.technicianId);
            this.appointmentCaseForm.get('technicianId')?.setValue(serviceGroup.technicianId);
            this.appointmentCaseForm.get('technicianGroupId')?.setValue(null);
          } else if (serviceGroup.technicianGroupId) {
            this.appointmentCaseForm.get('technicianGroupId')?.setValue(serviceGroup.technicianGroupId);
            this.appointmentCaseForm.get('techOrTechGroup')?.setValue(serviceGroup.technicianGroupId);
            this.appointmentCaseForm.get('technicianId')?.setValue(null);
          } else {
            this.appointmentCaseForm.get('technicianGroupId')?.setValue(null);
            this.appointmentCaseForm.get('techOrTechGroup')?.setValue(null);
          }
          this.appointmentCaseForm.get('serviceGroupId')?.setValue(serviceGroup.id);
          this.appointmentCaseForm.get('warrantySubmissionTypeId')?.setValue(serviceGroup.warrantySubmissionTypeId);
          this.appointmentCaseForm.get('estimatedTime')?.setValue(serviceGroup.estimatedTime);
          this.appointmentCaseForm.get('recommendedHours')?.setValue(serviceGroup.recommendedHours);
          this.appointmentCaseForm.get('requestDescription')?.setValue(serviceGroup.name);
          this.checkTenths(['estimatedTime', 'recommendedHours']);
        }
      });

    /**
     * When user enters a campaign number, automatically set the warrantySubType
     * to Campaign and disable Submission Type field to prevent changing the sub type.
     * Look for a campaign header with the entered value, if found, populate the form with
     * values.
     */
    this.caseFormCampaignNumberSub$ = this.appointmentCaseForm
      .get('campaignNumber')
      ?.valueChanges.pipe(
        takeUntil(this.destroy$),
        tap((campaignNumber: string) => {
          if (campaignNumber && campaignNumber.length > 0) {
            this.appointmentCaseForm.get('warrantySubmissionTypeId')?.setValue('CC', { emitEvent: false });
            this.appointmentCaseForm.get('warrantySubmissionTypeId')?.disable({ emitEvent: false });
          } else {
            this.appointmentCaseForm.get('campaignNumber')?.setValue(null, { emitEvent: false });
            this.appointmentCaseForm.get('warrantySubmissionTypeId')?.setValue(null, { emitEvent: false });
            this.appointmentCaseForm.get('warrantySubmissionTypeId')?.enable({ emitEvent: false });
          }
        }),
        filter((campaignNumber) => campaignNumber && campaignNumber.length > 0),
        debounceTime(1000)
      )
      .pipe(
        switchMap((campaignNumber) => {
          return this.apiService.get<CampaignHeader>(`v/2/fixed-operations/campaign-headers/${campaignNumber}`);
        })
      )
      .subscribe((campaign: CampaignHeader) => {
        if (campaign) {
          if (campaign.technicianId) {
            this.appointmentCaseForm.get('techOrTechGroup')?.setValue(campaign.technicianId);
            this.appointmentCaseForm.get('technicianId')?.setValue(campaign.technicianId);
          } else if (campaign.technicianGroupId) {
            this.appointmentCaseForm.get('technicianGroupId')?.setValue(campaign.technicianGroupId);
            this.appointmentCaseForm.get('techOrTechGroup')?.setValue(campaign.technicianGroupId);
          } else {
            this.appointmentCaseForm.get('technicianGroupId')?.setValue(null);
            this.appointmentCaseForm.get('techOrTechGroup')?.setValue(null);
          }
          this.appointmentCaseForm.get('estimatedTime')?.setValue(campaign.estimatedTime);
          this.appointmentCaseForm.get('recommendedHours')?.setValue(campaign.recommendedHours);
          this.appointmentCaseForm.get('opCode')?.setValue(campaign.operationCode);
          this.appointmentCaseForm.get('requestDescription')?.setValue(campaign.description);
          this.checkTenths(['estimatedTime', 'recommendedHours']);
        }
      });

    /**
     * When Submission Type changes, if Campaign is selected, we need to enforce Campaign number
     * If it's anything other than Campaign, disable the Campaign # field
     */
    this.warrantySubmissionTypeSub$ = this.appointmentCaseForm
      .get('warrantySubmissionTypeId')
      ?.valueChanges.pipe(
        takeUntil(this.destroy$),
        map((warrantySubmissionTypeId: string) => {
          const selectedWarrantyType = this.dialogData.dropdowns.submissionTypes.find(
            (subType: WarrantySubmissionType) => subType.id === warrantySubmissionTypeId
          );
          if (selectedWarrantyType) {
            if (!selectedWarrantyType.description.toLowerCase().includes('campaign')) {
              this.appointmentCaseForm.get('campaignNumber')?.setValue(null, { emitEvent: false });
              this.appointmentCaseForm.get('campaignNumber')?.disable({ emitEvent: false });
            } else {
              this.appointmentCaseForm.get('campaignNumber')?.setValidators(Validators.required);
              this.appointmentCaseForm.get('campaignNumber')?.enable({ emitEvent: false });
            }
            /**
             * If selected Submission Type is one of the warranty types, look for any vehicle warranties.
             * If found, determine if regular warranty has expired to inform the user.
             */
            if (selectedWarrantyType.description.toLowerCase().includes('warranty')) {
              combineLatest([
                this.apiService.get(`v/1/reporting/vehicles/workflow/warranties/${this.dialogData.vehicle.id}`),
                this.apiService.get(`v/1/vehicles/vehicles/${this.dialogData.vehicle.id}`, {
                  params: { embed: 'associate' },
                }),
              ])
                .pipe(
                  takeUntil(this.destroy$),
                  map(([warrantyInfo, vehicle]: [AllExtendedWarranty, Vehicle]) => {
                    return {
                      warrantyInfo: warrantyInfo,
                      vehicle: vehicle,
                    };
                  }),
                  tap((data) => {
                    if (data.warrantyInfo.regularWarranty) {
                      const date = moment(data.warrantyInfo.regularWarranty.effectiveDate).add(
                        data.warrantyInfo.regularWarranty.warrantyTerm,
                        'months'
                      );
                      if (
                        date.isAfter(new Date()) &&
                        data.warrantyInfo.regularWarranty.warrantyMileage > data.vehicle.currentMileage
                      ) {
                        this.basicWarrantyExpired$.next(false);
                      } else {
                        this.basicWarrantyExpired$.next(true);
                      }
                    } else {
                      this.basicWarrantyExpired$.next(false);
                    }
                  })
                )
                .subscribe();
            } else {
              this.basicWarrantyExpired$.next(false);
            }
          }
        })
      )
      .subscribe();
  }

  setFormValidation() {
    this.appointmentCaseForm.get('warrantySubmissionTypeId')?.setValidators(Validators.required);
    this.appointmentCaseForm
      .get('requestDescription')
      ?.setValidators([Validators.required, CustomValidators.noWhitespaceValidator]);
    this.appointmentCaseForm.get('techOrTechGroup')?.setValidators(Validators.required);
  }

  techOrGroupSelected(techId: string | number, technicians: Employee[], techGroups: TechnicianGroup[]) {
    if (technicians.findIndex((t) => t.associateId == techId) > -1) {
      const tech: Employee = technicians.find((t) => t.associateId == techId);
      this.appointmentCaseForm.get('technicianId')?.setValue(tech.associateId);
      this.appointmentCaseForm
        .get('technicianGroupName')
        ?.setValue(`${tech.embedded?.associate?.firstName} ${tech.embedded?.associate?.lastName} `);
      this.appointmentCaseForm.get('technicianGroupId')?.setValue(null);
    } else {
      const group: TechnicianGroup = techGroups.find((g) => g.id == techId);
      this.appointmentCaseForm.get('technicianGroupId')?.setValue(group.id);
      this.appointmentCaseForm.get('technicianGroupName')?.setValue(group.description);
      this.appointmentCaseForm.get('technicianId')?.setValue(null);
    }
  }

  serviceGroupSelected(serviceGroupId: string, serviceGroups: ServiceGroup[]) {
    const sg: ServiceGroup = serviceGroups.find((s) => s.id == serviceGroupId);
    this.appointmentCaseForm.get('warrantySubmissionTypeId')?.setValue(sg.warrantySubmissionTypeId);
    this.appointmentCaseForm.get('estimatedTime')?.setValue(sg.estimatedTime);
    this.appointmentCaseForm.get('opCode')?.setValue(sg.workOrderLocationOpCode);
    this.appointmentCaseForm.get('recommendedHours')?.setValue(sg.recommendedHours);
    this.appointmentCaseForm.get('requestDescription')?.setValue(sg.name);
    this.checkTenths(['estimatedTime', 'recommendedHours']);
  }

  /**
   * Ensures that the estimated/recommended time fields follow the X.Y format widely used in fixed ops
   *
   * @param formFields string array containing the form control names of the fields to be checked
   */
  checkTenths(formFields: string[]) {
    formFields.forEach((formField) => {
      const currentValue = this.appointmentCaseForm.get(formField)?.value
        ? this.appointmentCaseForm.get(formField)?.value.toString()
        : null;
      if (currentValue) {
        if (currentValue.length > 0 && !currentValue.includes('.')) {
          this.appointmentCaseForm.get(formField)?.setValue(currentValue + '.0');
        } else {
          this.appointmentCaseForm.get(formField)?.setValue(currentValue);
        }
      } else {
        this.appointmentCaseForm.get(formField)?.setValue('0.0');
      }
    });
  }

  cancel() {
    this.dialogRef.close();
  }

  save() {
    this.formSubmitted = true;
    this.checkTenths(['estimatedTime', 'recommendedHours']);
    this.appointmentCaseForm.markAllAsTouched();

    if (this.appointmentCaseForm.valid) {
      this.dialogRef.close({ appointmentCase: this.appointmentCaseForm.getRawValue() });
    }
  }

  serviceGroupSearch() {
    this.dialogRef.close({ appointmentCase: null, addManually: false });
  }

  validateNumber(event: any) {
    const key = event.key;
    const excludedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', '.'];
    if (!((key >= 0 && key <= 9) || excludedKeys.includes(key))) {
      event.preventDefault();
    }
  }
}
